/*!
 * Reblo Spokój — reblo.js
 * UI behaviour. Vanilla, defer-loaded, progressive enhancement.
 * Depends on cart.js only via document events (cart:updated, reblo:cart-drawer-open).
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------
  // Namespace + utils (cart.js may already have populated these)
  // ---------------------------------------------------------------------
  window.Reblo = window.Reblo || {};
  var R = window.Reblo;
  R.utils = R.utils || {};

  if (!R.utils.debounce) {
    R.utils.debounce = function (fn, ms) {
      var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function(){ fn.apply(c, a); }, ms); };
    };
  }
  if (!R.utils.throttle) {
    R.utils.throttle = function (fn, ms) {
      var last = 0, t;
      return function () {
        var now = Date.now(), a = arguments, c = this, rem = ms - (now - last);
        if (rem <= 0) { clearTimeout(t); t = null; last = now; fn.apply(c, a); }
        else if (!t) t = setTimeout(function(){ last = Date.now(); t = null; fn.apply(c, a); }, rem);
      };
    };
  }
  if (!R.utils.escapeHtml) {
    R.utils.escapeHtml = function (s) {
      if (s == null) return '';
      return String(s).replace(/[&<>"']/g, function (c) { return ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' })[c]; });
    };
  }
  if (!R.utils.getCookie) {
    R.utils.getCookie = function (name) {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    };
  }
  if (!R.utils.formatMoney) {
    R.utils.formatMoney = function (cents) {
      cents = parseInt(cents, 10) || 0;
      return (cents / 100).toFixed(2).replace('.', ',') + ' zł';
    };
  }

  var debounce = R.utils.debounce;
  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------------------------------------------------------------------
  // Focus trap helper
  // ---------------------------------------------------------------------
  function focusableWithin(root) {
    return Array.prototype.slice.call(root.querySelectorAll(
      'a[href],area[href],input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),button:not([disabled]),iframe,object,embed,[tabindex]:not([tabindex="-1"]),[contenteditable]'
    )).filter(function (el) { return el.offsetWidth || el.offsetHeight || el.getClientRects().length; });
  }

  function createFocusTrap(root) {
    var lastFocused = document.activeElement;
    function onKey(e) {
      if (e.key !== 'Tab') return;
      var f = focusableWithin(root);
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', onKey);
    var f = focusableWithin(root);
    if (f.length) f[0].focus();
    return function release() {
      document.removeEventListener('keydown', onKey);
      try { lastFocused && lastFocused.focus && lastFocused.focus(); } catch (e) {}
    };
  }

  function lockScroll(lock) {
    document.documentElement.classList.toggle('is-locked', !!lock);
  }

  // ---------------------------------------------------------------------
  // Generic drawer/modal opener (used by cart drawer, search, mobile nav, quick view)
  // ---------------------------------------------------------------------
  var openOverlays = []; // stack for ESC

  function openOverlay(el, opts) {
    if (!el || el.dataset.open === 'true') return;
    opts = opts || {};
    el.dataset.open = 'true';
    el.removeAttribute('hidden');
    el.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(function () { el.classList.add('is-open'); });
    lockScroll(true);
    var release = createFocusTrap(el);
    var entry = { el: el, release: release, onClose: opts.onClose };
    openOverlays.push(entry);
    return entry;
  }

  function closeOverlay(el) {
    var idx = -1;
    for (var i = openOverlays.length - 1; i >= 0; i--) {
      if (openOverlays[i].el === el) { idx = i; break; }
    }
    if (idx === -1) return;
    var entry = openOverlays.splice(idx, 1)[0];
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    el.dataset.open = 'false';
    // Wait briefly for transition before hidden (keeps a11y simple)
    setTimeout(function () {
      if (el.dataset.open !== 'true') el.setAttribute('hidden', '');
    }, 250);
    try { entry.release(); } catch (e) {}
    if (typeof entry.onClose === 'function') entry.onClose();
    if (!openOverlays.length) lockScroll(false);
  }

  function closeTopOverlay() {
    if (!openOverlays.length) return;
    var top = openOverlays[openOverlays.length - 1];
    closeOverlay(top.el);
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeTopOverlay();
  });

  // Generic close-button + backdrop binding
  document.addEventListener('click', function (e) {
    var closeBtn = e.target.closest && e.target.closest('[data-overlay-close], [data-cart-drawer-close], [data-search-modal-close], [data-mobile-nav-close], [data-quick-view-close], [data-drawer-backdrop]');
    if (!closeBtn) return;
    // Find nearest open overlay ancestor
    var sel = '[data-cart-drawer], [data-search-modal], [data-mobile-nav], [data-quick-view], [data-overlay]';
    var overlay = closeBtn.closest(sel);
    if (!overlay) {
      // backdrop case — find topmost
      if (openOverlays.length) overlay = openOverlays[openOverlays.length - 1].el;
    }
    if (overlay) {
      e.preventDefault();
      closeOverlay(overlay);
    }
  });

  // =====================================================================
  // 1. Cart drawer open
  // =====================================================================
  function initCartDrawer() {
    var drawer = document.getElementById('CartDrawer') || document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-cart-drawer-open]');
      if (!t) return;
      e.preventDefault();
      openOverlay(drawer);
    });
    document.addEventListener('reblo:cart-drawer-open', function () { openOverlay(drawer); });
  }

  // =====================================================================
  // 2. Search modal
  // =====================================================================
  function initSearchModal() {
    var modal = document.querySelector('[data-search-modal]');
    if (!modal) return;
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-search-modal-open]');
      if (!t) return;
      e.preventDefault();
      openOverlay(modal);
      var input = modal.querySelector('[data-predictive-search-input], input[type="search"]');
      if (input) setTimeout(function () { input.focus(); }, 60);
    });
  }

  // =====================================================================
  // 3. Predictive search
  // =====================================================================
  function initPredictiveSearch() {
    document.querySelectorAll('[data-predictive-search-input]').forEach(function (input) {
      var host = input.closest('[data-predictive-search-host]');
      var results = host ? host.querySelector('[data-predictive-search]') : null;
      if (!results) return;

      var searchUrl = (window.routes && window.routes.search_url) || '/search';

      function setSection(name, visible) {
        var sec = results.querySelector('[data-predictive-section="' + name + '"]');
        if (sec) sec.toggleAttribute('hidden', !visible);
      }
      function renderList(name, items, renderer) {
        var list = results.querySelector('[data-predictive-list="' + name + '"]');
        if (!list) return false;
        list.innerHTML = '';
        if (!items || !items.length) { setSection(name, false); return false; }
        items.forEach(function (item) {
          var li = document.createElement('li');
          li.innerHTML = renderer(item);
          list.appendChild(li);
        });
        setSection(name, true);
        return true;
      }

      var doSearch = debounce(function (q) {
        if (!q || q.length < 3) {
          results.setAttribute('hidden', '');
          var empty = results.querySelector('[data-predictive-empty]');
          if (empty) empty.setAttribute('hidden', '');
          return;
        }
        var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                  '&resources[type]=product,article,page' +
                  '&resources[options][unavailable_products]=last' +
                  '&resources[limit]=6';
        fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var res = (data && data.resources && data.resources.results) || {};
            var products = res.products || [];
            var articles = res.articles || [];
            var pages    = res.pages || [];

            var anyProducts = renderList('products', products, function (p) {
              var img = p.image ? '<img src="' + R.utils.escapeHtml(p.image) + '" alt="" loading="lazy" width="48" height="48">' : '';
              var price = p.price ? '<span class="ps-price">' + R.utils.formatMoney(Math.round(parseFloat(p.price) * 100)) + '</span>' : '';
              return '<a class="ps-item ps-product" href="' + R.utils.escapeHtml(p.url) + '">' +
                     img + '<span class="ps-title">' + R.utils.escapeHtml(p.title) + '</span>' + price + '</a>';
            });
            var anyArticles = renderList('articles', articles, function (a) {
              return '<a class="ps-item ps-article" href="' + R.utils.escapeHtml(a.url) + '">' + R.utils.escapeHtml(a.title) + '</a>';
            });
            var anyPages = renderList('pages', pages, function (p) {
              return '<a class="ps-item ps-page" href="' + R.utils.escapeHtml(p.url) + '">' + R.utils.escapeHtml(p.title) + '</a>';
            });

            var hasAny = anyProducts || anyArticles || anyPages;
            results.toggleAttribute('hidden', false);
            var empty = results.querySelector('[data-predictive-empty]');
            if (empty) empty.toggleAttribute('hidden', hasAny);

            var viewAll = results.querySelector('[data-predictive-view-all]');
            if (viewAll) {
              viewAll.href = searchUrl + '?q=' + encodeURIComponent(q);
              viewAll.toggleAttribute('hidden', !hasAny);
            }
          })
          .catch(function () { /* noop */ });
      }, 250);

      input.addEventListener('input', function () { doSearch(input.value.trim()); });
    });
  }

  // =====================================================================
  // 3b. Price range dual slider
  // =====================================================================
  function initPriceSliders() {
    document.querySelectorAll('[data-price-filter]').forEach(function (wrap) {
      var minRange  = wrap.querySelector('[data-price-min-range]');
      var maxRange  = wrap.querySelector('[data-price-max-range]');
      var minHidden = wrap.querySelector('[data-price-min-val]');
      var maxHidden = wrap.querySelector('[data-price-max-val]');
      var minLabel  = wrap.querySelector('[data-price-min-label]');
      var maxLabel  = wrap.querySelector('[data-price-max-label]');
      var fill      = wrap.querySelector('[data-price-fill]');
      if (!minRange || !maxRange) return;

      function updateFill() {
        var total = parseFloat(maxRange.max) - parseFloat(minRange.min);
        if (total <= 0) return;
        var left  = (parseFloat(minRange.value) - parseFloat(minRange.min)) / total * 100;
        var right = (parseFloat(maxRange.max) - parseFloat(maxRange.value)) / total * 100;
        if (fill) { fill.style.left = left + '%'; fill.style.right = right + '%'; }
      }

      function update() {
        var minVal = parseInt(minRange.value, 10);
        var maxVal = parseInt(maxRange.value, 10);
        if (minVal > maxVal) { minRange.value = maxVal; minVal = maxVal; }
        if (maxVal < minVal) { maxRange.value = minVal; maxVal = minVal; }
        var atMin = minVal <= parseInt(minRange.min, 10);
        var atMax = maxVal >= parseInt(maxRange.max, 10);
        if (minHidden) minHidden.value = atMin ? '' : minVal;
        if (maxHidden) maxHidden.value = atMax ? '' : maxVal;
        if (minLabel) minLabel.textContent = minVal + ' zł';
        if (maxLabel) maxLabel.textContent = maxVal + ' zł';
        updateFill();
      }

      function submitOwningForm() {
        var f = wrap.closest('form[data-collection-filters-form]');
        if (!f) return;
        // Dispatch a synthetic 'change' on the form so the AJAX delegate
        // (initCollectionFilters) picks it up like any other filter change.
        f.dispatchEvent(new Event('reblo:price-change', { bubbles: true }));
      }

      minRange.addEventListener('input', update);
      maxRange.addEventListener('input', update);
      minRange.addEventListener('change', submitOwningForm);
      maxRange.addEventListener('change', submitOwningForm);
      updateFill();
    });
  }

  // =====================================================================
  // 3c. Collection filters & sort — AJAX replacement, no full reload
  // =====================================================================
  function initCollectionFilters() {
    var section = document.querySelector('[data-collection-page]');
    if (!section) return;

    var loadToken = 0;

    function fetchAndUpdate(url) {
      var myToken = ++loadToken;
      section.classList.add('is-loading');
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          if (myToken !== loadToken) return; // a newer request superseded us
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newSection = doc.querySelector('[data-collection-page]');
          if (!newSection) { window.location.href = url; return; }
          section.innerHTML = newSection.innerHTML;
          if (window.history && window.history.pushState) {
            window.history.pushState({ rebloCollection: true }, '', url);
          }
          // Re-init the price slider on the new DOM (the change/input
          // listeners attached to the old range inputs were discarded).
          initPriceSliders();
          // Let other modules (cards, lazy images, etc.) re-bind if needed.
          document.dispatchEvent(new CustomEvent('reblo:collection:updated', { detail: { url: url } }));
        })
        .catch(function () {
          // Network failure — fall back to a real navigation.
          window.location.href = url;
        })
        .then(function () { section.classList.remove('is-loading'); });
    }

    function buildFilterUrl() {
      var f = section.querySelector('form[data-collection-filters-form]');
      if (!f) return null;
      var fd = new FormData(f);
      var params = new URLSearchParams();
      fd.forEach(function (val, key) {
        var v = (val == null ? '' : String(val)).trim();
        if (v !== '') params.append(key, v);
      });
      var base = f.getAttribute('action') || window.location.pathname;
      return base + (params.toString() ? '?' + params.toString() : '');
    }

    function submitFilterForm() {
      var url = buildFilterUrl();
      if (url) fetchAndUpdate(url);
    }

    // Change events inside the section (filters + sort).
    section.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      if (t.matches && t.matches('[data-collection-sort-select]')) {
        // Sort-by: rebuild URL preserving filters (the select lives in its
        // own form; submitting through the filter form keeps everything in
        // sync because the filter form has its own hidden sort_by input).
        var f = section.querySelector('form[data-collection-filters-form]');
        if (f) {
          var hidden = f.querySelector('input[name="sort_by"]');
          if (hidden) hidden.value = t.value;
          submitFilterForm();
        } else {
          var u = new URL(window.location.href);
          u.searchParams.set('sort_by', t.value);
          fetchAndUpdate(u.toString());
        }
        return;
      }
      if (t.closest && t.closest('form[data-collection-filters-form]')) {
        var type = (t.type || '').toLowerCase();
        if (type === 'checkbox' || type === 'radio') {
          submitFilterForm();
        }
      }
    });

    // Price slider releases dispatch a synthetic event on the form.
    section.addEventListener('reblo:price-change', function () { submitFilterForm(); });

    // Apply button (explicit form submit).
    section.addEventListener('submit', function (e) {
      var f = e.target;
      if (f && f.matches && f.matches('form[data-collection-filters-form]')) {
        e.preventDefault();
        submitFilterForm();
      }
    });

    // Active chip removals + pagination links + "clear all".
    section.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      if (a.matches('[data-collection-filters-toggle], [data-collection-filters-close]')) return;
      if (a.matches('.coll-chip, .pagination a, .pagination-prev, .pagination-next, a.pagination-num')) {
        e.preventDefault();
        fetchAndUpdate(a.getAttribute('href'));
      }
    });

    // Browser back / forward.
    window.addEventListener('popstate', function (e) {
      if (e.state && e.state.rebloCollection) {
        fetchAndUpdate(window.location.href);
      }
    });

    // Mobile filter panel toggle (delegated on document — toggles live
    // outside the section as well in some layouts).
    document.addEventListener('click', function (e) {
      var toggle = e.target.closest && e.target.closest('[data-collection-filters-toggle]');
      if (toggle) {
        var panelId = toggle.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        if (!panel) return;
        var opening = !panel.classList.contains('is-open');
        panel.classList.toggle('is-open', opening);
        toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
        return;
      }
      var closeBtn = e.target.closest && e.target.closest('[data-collection-filters-close]');
      if (closeBtn) {
        var p = closeBtn.closest('[data-collection-filters]');
        if (p) p.classList.remove('is-open');
        var tog = document.querySelector('[data-collection-filters-toggle]');
        if (tog) tog.setAttribute('aria-expanded', 'false');
      }
    });
  }

  // =====================================================================
  // 4. Quick view
  // =====================================================================
  function initQuickView() {
    var modal = document.querySelector('[data-quick-view]');
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-quick-view-open]');
      if (!btn) return;
      var handle = btn.getAttribute('data-product-handle') || btn.getAttribute('data-quick-view-open');
      if (!handle) return;
      e.preventDefault();
      if (!modal) { window.location.href = '/products/' + handle; return; }
      var body = modal.querySelector('[data-quick-view-body]');
      if (body) body.innerHTML = '<div class="qv-loading" aria-live="polite">…</div>';
      openOverlay(modal);
      var close = modal.querySelector('[data-quick-view-close]');
      if (close) setTimeout(function () { close.focus(); }, 60);
      fetch('/products/' + handle + '?view=quick-view', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('quick-view not available');
          return r.text();
        })
        .then(function (html) {
          if (!body) return;
          // Extract inner content if Shopify returns full doc — find <main> or fall back
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('[data-quick-view-content]') || doc.querySelector('main') || doc.body;
          body.innerHTML = inner ? inner.innerHTML : html;
        })
        .catch(function () {
          // Fallback: redirect to PDP
          window.location.href = '/products/' + handle;
        });
    });
  }

  // =====================================================================
  // 5. Quantity steppers
  // =====================================================================
  function initQtySteppers(root) {
    (root || document).querySelectorAll('[data-qty-decrease], [data-qty-increase]').forEach(function (btn) {
      if (btn.__rebloBound) return;
      btn.__rebloBound = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var dir = btn.matches('[data-qty-increase]') ? 1 : -1;
        var input = btn.parentElement && btn.parentElement.querySelector('input[type="number"], input[type="text"]');
        if (!input) {
          // Search siblings broadly
          var p = btn.closest('[data-qty-wrap], .qty, .quantity');
          input = p && p.querySelector('input');
        }
        if (!input) return;
        var min = parseInt(input.min, 10); if (isNaN(min)) min = 0;
        var max = parseInt(input.max, 10); if (isNaN(max)) max = Infinity;
        var cur = parseInt(input.value, 10) || 0;
        var next = Math.min(max, Math.max(min, cur + dir));
        if (next === cur) return;
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  // =====================================================================
  // 6. Sticky header shadow
  // =====================================================================
  function initStickyHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    // Half-sticky "headroom": the announcement strip sits in normal flow directly
    // above the header, so its height is the header's natural top offset. Above it
    // the header rides in flow (announcement visible); once we scroll past it the
    // header is pinned (position:sticky, top:0) and we hide it on scroll-down /
    // reveal it on scroll-up. Reaching the very top releases sticky → the header
    // drops back below the announcement bar to its real position.
    var ann = document.querySelector('[data-announcement-bar]');
    var lastY = window.scrollY;
    var DELTA = 4; // ignore sub-pixel/jitter scrolls
    var ticking = false;
    function annHeight() { return ann ? ann.offsetHeight : 0; }
    function update() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 12);
      // Within the announcement strip's band → always show the header (in flow).
      if (y <= annHeight() + DELTA) {
        header.classList.remove('header--hidden');
      } else if (Math.abs(y - lastY) > DELTA) {
        // Pinned: hide when going down, reveal when going up.
        header.classList.toggle('header--hidden', y > lastY);
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  // =====================================================================
  // 7. Snap carousels
  // =====================================================================
  function initCarousel(root) {
    if (root.__rebloCarousel) return;
    root.__rebloCarousel = true;
    var track = root.querySelector('[data-carousel-track]');
    if (!track) return;
    var scope = root.closest('section') || root.parentNode || root;
    var prev = root.querySelector('[data-carousel-prev]') || scope.querySelector('[data-carousel-prev]');
    var next = root.querySelector('[data-carousel-next]') || scope.querySelector('[data-carousel-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-carousel-dot]'));
    var autoplay = parseFloat(root.getAttribute('data-carousel-autoplay-seconds'));
    var timer = null;

    function step(dir) {
      var w = track.clientWidth;
      track.scrollBy({ left: dir * w, top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }
    function scrollToIndex(i) {
      var children = track.children;
      if (!children[i]) return;
      track.scrollTo({ left: children[i].offsetLeft, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }
    function updateDots() {
      if (!dots.length) return;
      var sl = track.scrollLeft, w = track.clientWidth;
      var active = Math.round(sl / w);
      dots.forEach(function (d, i) {
        d.classList.toggle('is-active', i === active);
        d.setAttribute('aria-current', i === active ? 'true' : 'false');
      });
    }

    // Disable prev/next at the scroll bounds (and both when nothing overflows),
    // so the arrows reflect whether there is anything to scroll.
    function updateArrows() {
      if (!prev && !next) return;
      var maxScroll = track.scrollWidth - track.clientWidth - 2;
      var noOverflow = maxScroll <= 0;
      if (prev) prev.disabled = noOverflow || track.scrollLeft <= 2;
      if (next) next.disabled = noOverflow || track.scrollLeft >= maxScroll;
    }

    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        var i = parseInt(d.getAttribute('data-index'), 10) || 0;
        scrollToIndex(i);
      });
    });
    track.addEventListener('scroll', R.utils.throttle(function () { updateDots(); updateArrows(); }, 100), { passive: true });
    updateDots();
    updateArrows();
    // Re-measure once images/layout settle and on resize (scrollWidth changes).
    window.addEventListener('load', updateArrows);
    window.addEventListener('resize', R.utils.throttle(updateArrows, 150), { passive: true });

    function startAuto() {
      if (!autoplay || isNaN(autoplay) || autoplay <= 0 || prefersReducedMotion) return;
      stopAuto();
      timer = setInterval(function () {
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
        if (atEnd) track.scrollTo({ left: 0, behavior: 'smooth' });
        else step(1);
      }, autoplay * 1000);
    }
    function stopAuto() { if (timer) { clearInterval(timer); timer = null; } }
    root.addEventListener('mouseenter', stopAuto);
    root.addEventListener('mouseleave', startAuto);
    root.addEventListener('focusin', stopAuto);
    root.addEventListener('focusout', startAuto);
    startAuto();
  }
  function initCarousels(root) {
    (root || document).querySelectorAll('[data-carousel]').forEach(initCarousel);
  }
  // Centre-mode carousel: one card is centred + emphasised, neighbours recede.
  // Transform-based so we can (a) keep the active card centred, (b) slide a
  // clicked card to the middle, and (c) loop infinitely by shuffling the
  // active card back to the DOM middle after each move. Used by video
  // testimonials.
  function initCenterCarousel(root) {
    if (root.__rebloCenter) return;
    root.__rebloCenter = true;
    var track = root.querySelector('[data-carousel-track]');
    if (!track) return;
    var viewport = track.parentNode; // .vt-viewport (overflow:hidden)
    var N = track.children.length;
    if (!N) return;
    var scope = root.closest('section') || root;
    var prevBtn = root.querySelector('[data-carousel-prev]') || scope.querySelector('[data-carousel-prev]');
    var nextBtn = root.querySelector('[data-carousel-next]') || scope.querySelector('[data-carousel-next]');
    var loop = N >= 3; // looping/centre-shuffle only makes sense with enough cards
    var active = track.children[0];
    var animating = false;

    function setTransition(on) {
      track.style.transition = on && !prefersReducedMotion ? 'transform .45s cubic-bezier(.4,.01,.2,1)' : 'none';
    }
    function paint() {
      var children = track.children;
      for (var i = 0; i < children.length; i++) {
        children[i].classList.toggle('is-active', children[i] === active);
      }
    }
    function center(animate) {
      setTransition(animate);
      var x = viewport.clientWidth / 2 - (active.offsetLeft + active.offsetWidth / 2);
      track.style.transform = 'translate3d(' + x + 'px,0,0)';
      paint();
    }
    // After a move settles, shuffle DOM so `active` sits at the middle index,
    // compensating the transform instantly so nothing visually jumps. This is
    // what makes the loop endless in both directions.
    function rebalance() {
      if (!loop) return;
      var target = Math.floor(track.children.length / 2);
      var idx = Array.prototype.indexOf.call(track.children, active);
      var diff = idx - target;
      if (!diff) return;
      var step = track.children[1].offsetLeft - track.children[0].offsetLeft;
      if (diff > 0) {
        for (var i = 0; i < diff; i++) track.appendChild(track.firstElementChild);
      } else {
        for (var j = 0; j < -diff; j++) track.insertBefore(track.lastElementChild, track.firstElementChild);
      }
      // active.offsetLeft shifted by diff*step; cancel it in the transform.
      setTransition(false);
      var cur = viewport.clientWidth / 2 - (active.offsetLeft + active.offsetWidth / 2);
      track.style.transform = 'translate3d(' + cur + 'px,0,0)';
    }
    function settle() { if (!animating) return; animating = false; rebalance(); }

    function goTo(el, animate) {
      if (!el || el === active) return;
      active = el;
      animating = true;
      center(animate !== false);
      // Fallback in case transitionend never fires (reduced motion, or the
      // target transform equals the current one).
      setTimeout(settle, prefersReducedMotion ? 30 : 520);
    }
    function move(dir) {
      var el = dir > 0
        ? (active.nextElementSibling || track.firstElementChild)
        : (active.previousElementSibling || track.lastElementChild);
      goTo(el, true);
    }

    track.addEventListener('transitionend', function (e) {
      if (e.target === track && e.propertyName === 'transform') settle();
    });
    if (prevBtn) prevBtn.addEventListener('click', function () { if (!animating) move(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { if (!animating) move(1); });

    // Swipe / drag to navigate (touch + mouse). A horizontal drag past the
    // threshold steps the carousel and suppresses the tap-to-play that would
    // otherwise fire when the gesture ends.
    var swStartX = 0, swStartY = 0, swActive = false, swiped = false;
    viewport.addEventListener('pointerdown', function (e) {
      swActive = true; swiped = false;
      swStartX = e.clientX; swStartY = e.clientY;
    });
    function endSwipe(e) {
      if (!swActive) return;
      swActive = false;
      var dx = e.clientX - swStartX, dy = e.clientY - swStartY;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        if (!animating) move(dx < 0 ? 1 : -1);
      }
    }
    viewport.addEventListener('pointerup', endSwipe);
    viewport.addEventListener('pointercancel', function () { swActive = false; });

    // Card clicks: centre the clicked card and play it; click the centred
    // (playing) card again to stop. Capture phase so the generic facade and
    // product links inside don't fire first.
    root.addEventListener('click', function (e) {
      if (swiped) { swiped = false; e.preventDefault(); e.stopPropagation(); return; }
      var card = e.target.closest('.vt-card');
      if (!card || !track.contains(card)) return;
      if (e.target.closest('a')) return; // let "Link do produktu" work
      e.preventDefault();
      e.stopPropagation();
      var media = card.querySelector('[data-video-facade]');
      if (card === active) {
        if (media) {
          if (media.classList.contains('is-playing')) R.video.reset(media);
          else R.video.play(media);
        }
      } else if (!animating) {
        goTo(card, true);
        if (media) R.video.play(media);
      }
    }, true);

    // Re-centre the active card whenever layout changes. This matters because
    // the section stylesheet loads asynchronously (media=print/onload), so at
    // first paint the flex sizes aren't applied yet — without this the initial
    // transform is measured against the wrong widths (active ends up off-centre
    // and the first arrow click jumps two cards).
    function recenter() { if (!animating) center(false); }
    window.addEventListener('resize', R.utils.throttle(recenter, 120));
    window.addEventListener('load', recenter);
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(R.utils.throttle(recenter, 60));
      ro.observe(viewport);
      ro.observe(active);
    }
    // Start with the first card centred but flanked by wrap-around neighbours
    // (move trailing cards to the front so it sits at the DOM middle).
    if (loop) {
      var target = Math.floor(N / 2);
      for (var k = 0; k < target; k++) track.insertBefore(track.lastElementChild, track.firstElementChild);
    }
    center(false);
    requestAnimationFrame(recenter);
  }
  function initCenterCarousels(root) {
    (root || document).querySelectorAll('[data-carousel-center]').forEach(initCenterCarousel);
  }

  // =====================================================================
  // 8. Accordions
  // =====================================================================
  function initAccordion(root) {
    if (root.__rebloAccordion) return;
    root.__rebloAccordion = true;
    var mode = root.getAttribute('data-accordion-mode'); // 'single' or null
    var triggers = root.querySelectorAll('[data-accordion-trigger]');

    function setOpen(trigger, open) {
      var panelId = trigger.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : trigger.nextElementSibling;
      if (!panel) return;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        if (prefersReducedMotion) {
          panel.style.maxHeight = 'none';
        } else {
          // Animate 0 -> content height, then release to 'none' so the panel
          // sizes naturally afterwards and can never clip or overlap the next
          // item if its content reflows (fonts/async CSS/responsive).
          var release = function (e) {
            if (e.target !== panel || e.propertyName !== 'max-height') return;
            panel.removeEventListener('transitionend', release);
            if (trigger.getAttribute('aria-expanded') === 'true') panel.style.maxHeight = 'none';
          };
          panel.addEventListener('transitionend', release);
          panel.style.maxHeight = '0px';
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              panel.style.maxHeight = panel.scrollHeight + 'px';
            });
          });
        }
      } else {
        // Snap current height before animating to 0 (avoids jump if maxHeight was 'none')
        panel.style.maxHeight = panel.scrollHeight + 'px';
        requestAnimationFrame(function () {
          panel.style.maxHeight = prefersReducedMotion ? '0px' : '0px';
        });
      }
    }

    // Set explicit maxHeight on initially-open panels so CSS transition has a concrete start value
    triggers.forEach(function (trigger) {
      var panelId = trigger.getAttribute('aria-controls');
      var panel = panelId ? document.getElementById(panelId) : trigger.nextElementSibling;
      if (panel && trigger.getAttribute('aria-expanded') === 'true' && !panel.style.maxHeight) {
        panel.style.maxHeight = 'none';
      }
    });

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var open = trigger.getAttribute('aria-expanded') !== 'true';
        if (mode === 'single' && open) {
          triggers.forEach(function (t) { if (t !== trigger) setOpen(t, false); });
        }
        setOpen(trigger, open);
      });
    });
  }
  function initAccordions(root) {
    (root || document).querySelectorAll('[data-accordion]').forEach(initAccordion);
  }

  // =====================================================================
  // 9. Tabs
  // =====================================================================
  function initTabs(root) {
    if (root.__rebloTabs) return;
    root.__rebloTabs = true;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tabs-tab]'));
    if (!tabs.length) return;

    function activate(idx) {
      tabs.forEach(function (t, i) {
        var on = i === idx;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        t.classList.toggle('is-active', on);
        var panelId = t.getAttribute('aria-controls');
        var panel = panelId ? document.getElementById(panelId) : null;
        if (panel) {
          panel.hidden = !on;
          panel.classList.toggle('is-active', on);
        }
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { activate(i); tab.focus(); });
      tab.addEventListener('keydown', function (e) {
        var idx = i;
        switch (e.key) {
          case 'ArrowRight': idx = (i + 1) % tabs.length; break;
          case 'ArrowLeft':  idx = (i - 1 + tabs.length) % tabs.length; break;
          case 'Home':       idx = 0; break;
          case 'End':        idx = tabs.length - 1; break;
          default: return;
        }
        e.preventDefault();
        activate(idx);
        tabs[idx].focus();
      });
    });

    // Activate first by default if none active
    var initial = tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; });
    activate(initial >= 0 ? initial : 0);
  }
  function initAllTabs(root) {
    (root || document).querySelectorAll('[data-tabs]').forEach(initTabs);
  }

  // =====================================================================
  // 10. Variant picker
  // =====================================================================
  function initVariantPicker(root) {
    if (root.__rebloVariant) return;
    root.__rebloVariant = true;
    var jsonEl = root.querySelector('[data-variant-json]');
    if (!jsonEl) return;
    var variants;
    try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    if (!Array.isArray(variants) || !variants.length) return;

    var handle = root.getAttribute('data-product-handle');
    var sectionId = root.getAttribute('data-section-id');
    var idInput = root.querySelector('[data-variant-id-input]');
    var fallbackSelect = root.querySelector('[data-variant-select]');
    var priceEl = root.querySelector('[data-product-price]') || document.querySelector('[data-product-price][data-section-id="' + sectionId + '"]');
    var compareEl = root.querySelector('[data-product-compare-price]') || document.querySelector('[data-product-compare-price][data-section-id="' + sectionId + '"]');
    var buyText = root.querySelector('[data-buybox-text]');
    var addBtn = root.querySelector('[type="submit"]');

    function readSelectedOptions() {
      var opts = [];
      var groups = root.querySelectorAll('[data-option-index]');
      if (groups.length) {
        groups.forEach(function (g) {
          var idx = parseInt(g.getAttribute('data-option-index'), 10);
          var checked = g.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
          var sel = g.querySelector('select');
          if (checked) opts[idx] = checked.value;
          else if (sel) opts[idx] = sel.value;
        });
      } else {
        // Fall back: read by name="options[Name]"
        var radios = root.querySelectorAll('input[type="radio"]:checked, select');
        radios.forEach(function (i, n) { opts.push(i.value); });
      }
      return opts;
    }

    function findMatch(opts) {
      return variants.find(function (v) {
        return (v.options || []).every(function (o, i) { return o === opts[i]; });
      });
    }

    function applyVariant(v) {
      if (!v) {
        if (addBtn) addBtn.disabled = true;
        if (buyText) buyText.textContent = buyText.getAttribute('data-text-unavailable') || 'Niedostępne';
        return;
      }
      if (idInput) idInput.value = v.id;
      if (fallbackSelect) fallbackSelect.value = v.id;

      if (addBtn) addBtn.disabled = !v.available;
      if (buyText) {
        if (!v.available) buyText.textContent = buyText.getAttribute('data-text-soldout') || 'Wyprzedane';
        else buyText.textContent = buyText.getAttribute('data-text-add') || 'Dodaj do koszyka';
      }

      // URL update
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        history.replaceState({}, '', url.toString());
      } catch (e) {}

      // Featured media
      if (v.featured_media) {
        document.dispatchEvent(new CustomEvent('reblo:variant-media-change', {
          detail: { variant: v, media: v.featured_media }
        }));
      }

      // Section render API for price block
      if (handle && sectionId) {
        fetch('/products/' + handle + '?variant=' + v.id + '&section_id=' + encodeURIComponent(sectionId), { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (html) {
            if (!html) return;
            var doc = new DOMParser().parseFromString(html, 'text/html');
            ['[data-product-price]', '[data-product-compare-price]', '[data-buybox-text]'].forEach(function (sel) {
              var src = doc.querySelector(sel);
              var dst = root.querySelector(sel);
              if (src && dst) dst.innerHTML = src.innerHTML;
            });
          })
          .catch(function () {
            // Fallback: in-place format
            if (priceEl) priceEl.textContent = R.utils.formatMoney(v.price);
            if (compareEl) {
              if (v.compare_at_price && v.compare_at_price > v.price) {
                compareEl.textContent = R.utils.formatMoney(v.compare_at_price);
                compareEl.removeAttribute('hidden');
              } else {
                compareEl.setAttribute('hidden', '');
              }
            }
          });
      } else {
        if (priceEl) priceEl.textContent = R.utils.formatMoney(v.price);
        if (compareEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            compareEl.textContent = R.utils.formatMoney(v.compare_at_price);
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
      }
    }

    root.addEventListener('change', function (e) {
      if (!e.target.matches('input[type="radio"], select')) return;
      applyVariant(findMatch(readSelectedOptions()));
    });

    // Init: match URL ?variant=… or first available
    var initial;
    try {
      var u = new URL(window.location.href);
      var vId = parseInt(u.searchParams.get('variant'), 10);
      if (vId) initial = variants.find(function (v) { return v.id === vId; });
    } catch (e) {}
    if (initial) applyVariant(initial);
  }
  function initVariantPickers(root) {
    (root || document).querySelectorAll('[data-variant-picker]').forEach(initVariantPicker);
  }

  // =====================================================================
  // 11. Mobile nav
  // =====================================================================
  function initMobileNav() {
    var nav = document.getElementById('MobileNav') || document.querySelector('[data-mobile-nav]');
    if (!nav) return;
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-mobile-nav-open]');
      if (!t) return;
      e.preventDefault();
      openOverlay(nav);
    });
  }

  // =====================================================================
  // 12. Mega menu (close other open <details data-megamenu>)
  // =====================================================================
  function initMegaMenu() {
    var nodes = document.querySelectorAll('details[data-megamenu]');
    nodes.forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        nodes.forEach(function (other) { if (other !== d && other.open) other.open = false; });
      });
    });
    // Close on click outside
    document.addEventListener('click', function (e) {
      nodes.forEach(function (d) {
        if (d.open && !d.contains(e.target)) d.open = false;
      });
    });
  }

  // =====================================================================
  // 13. Countdown
  // =====================================================================
  function initCountdown(el) {
    if (el.__rebloCountdown) return;
    el.__rebloCountdown = true;
    var endStr = el.getAttribute('data-countdown-end');
    var end = endStr ? new Date(endStr).getTime() : NaN;
    if (isNaN(end)) return;
    var dEl = el.querySelector('.countdown-days');
    var hEl = el.querySelector('.countdown-hours');
    var mEl = el.querySelector('.countdown-mins');
    var sEl = el.querySelector('.countdown-secs');
    var expired = document.querySelector('[data-countdown-expired]') ||
                  (el.parentElement && el.parentElement.querySelector('[data-countdown-expired]'));
    function pad(n) { return n < 10 ? '0' + n : '' + n; }
    function tick() {
      var diff = end - Date.now();
      if (diff <= 0) {
        if (dEl) dEl.textContent = '00';
        if (hEl) hEl.textContent = '00';
        if (mEl) mEl.textContent = '00';
        if (sEl) sEl.textContent = '00';
        el.setAttribute('hidden', '');
        if (expired) expired.removeAttribute('hidden');
        clearInterval(interval);
        return;
      }
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      if (dEl) dEl.textContent = pad(d);
      if (hEl) hEl.textContent = pad(h);
      if (mEl) mEl.textContent = pad(m);
      if (sEl) sEl.textContent = pad(s);
    }
    tick();
    var interval = setInterval(tick, 1000);
  }
  function initCountdowns(root) {
    (root || document).querySelectorAll('[data-countdown]').forEach(initCountdown);
  }

  // =====================================================================
  // 14. Count-up stats
  // =====================================================================
  function initCountUp(root) {
    var els = (root || document).querySelectorAll('[data-countup]');
    if (!els.length) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        observer.unobserve(el);
        var target = parseFloat(el.getAttribute('data-target')) || 0;
        if (prefersReducedMotion) { el.textContent = formatTarget(target, el); return; }
        var duration = 1500;
        var startTs;
        function easeOut(t) { return 1 - Math.pow(1 - t, 3); }
        function step(ts) {
          if (!startTs) startTs = ts;
          var p = Math.min(1, (ts - startTs) / duration);
          var v = target * easeOut(p);
          el.textContent = formatTarget(v, el);
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.3 });
    els.forEach(function (el) { observer.observe(el); });
  }
  function formatTarget(v, el) {
    var decimals = parseInt(el.getAttribute('data-decimals'), 10);
    if (isNaN(decimals)) decimals = 0;
    var suffix = el.getAttribute('data-suffix') || '';
    var prefix = el.getAttribute('data-prefix') || '';
    return prefix + v.toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + suffix;
  }

  // =====================================================================
  // 15. Back to top
  // =====================================================================
  function initBackToTop() {
    var btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    var ticking = false;
    function update() {
      btn.classList.toggle('is-visible', window.scrollY > 600);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });
    update();
  }

  // =====================================================================
  // 16. Lightbox
  // =====================================================================
  function initLightbox() {
    var triggers = document.querySelectorAll('[data-lightbox]');
    if (!triggers.length) return;

    // Group by data-lightbox value (string id) for prev/next sets
    var groups = {};
    triggers.forEach(function (t) {
      var g = t.getAttribute('data-lightbox') || 'default';
      (groups[g] = groups[g] || []).push(t);
    });

    var overlay = document.createElement('div');
    overlay.className = 'reblo-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('hidden', '');
    overlay.innerHTML =
      '<button type="button" class="reblo-lightbox__close" data-lb-close aria-label="Zamknij">×</button>' +
      '<button type="button" class="reblo-lightbox__prev" data-lb-prev aria-label="Poprzednie">‹</button>' +
      '<button type="button" class="reblo-lightbox__next" data-lb-next aria-label="Następne">›</button>' +
      '<figure class="reblo-lightbox__figure"><img alt="" data-lb-img></figure>';
    document.body.appendChild(overlay);

    var currentGroup = null, currentIdx = 0;
    function show(group, idx) {
      currentGroup = group;
      var items = groups[group];
      if (!items || !items[idx]) return;
      currentIdx = idx;
      var t = items[idx];
      var src = t.getAttribute('href') || t.getAttribute('data-lb-src') || (t.querySelector('img') && t.querySelector('img').src);
      var alt = t.getAttribute('data-alt') || (t.querySelector('img') && t.querySelector('img').alt) || '';
      var img = overlay.querySelector('[data-lb-img]');
      img.src = src; img.alt = alt;
      openOverlay(overlay);
    }

    triggers.forEach(function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        var g = t.getAttribute('data-lightbox') || 'default';
        var i = groups[g].indexOf(t);
        show(g, i);
      });
    });

    overlay.addEventListener('click', function (e) {
      if (e.target.matches('[data-lb-close]') || e.target === overlay) { closeOverlay(overlay); }
      else if (e.target.matches('[data-lb-prev]')) {
        var items = groups[currentGroup];
        show(currentGroup, (currentIdx - 1 + items.length) % items.length);
      } else if (e.target.matches('[data-lb-next]')) {
        var itemsN = groups[currentGroup];
        show(currentGroup, (currentIdx + 1) % itemsN.length);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (overlay.getAttribute('hidden') !== null) return;
      if (e.key === 'ArrowLeft') overlay.querySelector('[data-lb-prev]').click();
      if (e.key === 'ArrowRight') overlay.querySelector('[data-lb-next]').click();
    });
  }

  // =====================================================================
  // 17. Video facade
  // =====================================================================
  function ytVimeoEmbed(url) {
    // Returns an autoplay embed URL for YouTube/Vimeo, or null if not recognised.
    var m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (m) return 'https://www.youtube.com/embed/' + m[1] + '?autoplay=1&rel=0&playsinline=1';
    m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (m) return 'https://player.vimeo.com/video/' + m[1] + '?autoplay=1';
    return null;
  }
  function isDirectVideo(url) {
    return /\.(mp4|webm|ogv|ogg|m3u8)(\?|#|$)/i.test(url) || /cdn\.shopify\.com\/.*\/video\//i.test(url) || /\/video\.shopify\//i.test(url);
  }
  function resetVideoFacade(el) {
    // Restore the facade to its poster + play-button state. Removing the
    // <video>/<iframe> node also stops playback (incl. YouTube/Vimeo).
    if (!el.classList.contains('is-playing')) return;
    if (el.__rebloVFHtml != null) el.innerHTML = el.__rebloVFHtml;
    el.classList.remove('is-playing');
  }
  // Build + play the video inside a facade container (used by the generic
  // facade click handler and by the centre-mode carousel). Stops any other
  // playing facade first so only one runs at a time.
  function playVideoFacade(el) {
    if (el.classList.contains('is-playing')) return;
    document.querySelectorAll('[data-video-facade].is-playing').forEach(function (other) {
      if (other !== el) resetVideoFacade(other);
    });
    var external = el.getAttribute('data-video-external');
    var src = el.getAttribute('data-video-src');
    var type = el.getAttribute('data-video-type') || '';
    var node, embed;
    if (external && (embed = ytVimeoEmbed(external))) {
      node = document.createElement('iframe');
      node.src = embed;
      node.setAttribute('frameborder', '0');
      node.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      node.setAttribute('allowfullscreen', '');
      node.setAttribute('title', el.getAttribute('data-title') || 'Video');
    } else if (src && (type === 'video' || isDirectVideo(src))) {
      node = document.createElement('video');
      node.src = src; node.controls = false; node.autoplay = true; node.playsInline = true; node.muted = false;
      node.setAttribute('preload', 'metadata');
    } else if (src && (embed = ytVimeoEmbed(src))) {
      node = document.createElement('iframe');
      node.src = embed;
      node.setAttribute('frameborder', '0');
      node.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      node.setAttribute('allowfullscreen', '');
      node.setAttribute('title', el.getAttribute('data-title') || 'Video');
    } else if (src) {
      node = document.createElement('iframe');
      node.src = src;
      node.setAttribute('frameborder', '0');
      node.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      node.setAttribute('allowfullscreen', '');
      node.setAttribute('loading', 'lazy');
      node.setAttribute('title', el.getAttribute('data-title') || 'Video');
    } else {
      return;
    }
    // Fill the styled facade container (keeps aspect-ratio, radius, overflow).
    node.style.position = 'absolute';
    node.style.inset = '0';
    node.style.width = '100%';
    node.style.height = '100%';
    node.style.border = '0';
    node.style.objectFit = 'cover';
    if (el.__rebloVFHtml == null) el.__rebloVFHtml = el.innerHTML;
    el.classList.add('is-playing');
    el.innerHTML = '';
    el.appendChild(node);
    var play = node.play && node.play();
    if (play && play.catch) { play.catch(function () {}); }
  }
  R.video = { play: playVideoFacade, reset: resetVideoFacade };
  function initVideoFacade(root) {
    (root || document).querySelectorAll('[data-video-facade]').forEach(function (el) {
      if (el.__rebloVF) return;
      el.__rebloVF = true;
      // Centre-mode carousels own their card clicks; skip those here.
      if (el.closest('[data-carousel-center]')) return;
      el.addEventListener('click', function () {
        // Click a playing video to stop it (back to its first-frame poster).
        if (el.classList.contains('is-playing')) { resetVideoFacade(el); return; }
        playVideoFacade(el);
      });
    });
  }

  // =====================================================================
  // 18. Map facade
  // =====================================================================
  function initMapFacade(root) {
    (root || document).querySelectorAll('[data-map-facade]').forEach(function (el) {
      if (el.__rebloMF) return;
      el.__rebloMF = true;
      el.addEventListener('click', function () {
        var src = el.getAttribute('data-map-src');
        if (!src) return;
        var iframe = document.createElement('iframe');
        iframe.src = src;
        iframe.setAttribute('loading', 'lazy');
        iframe.setAttribute('title', el.getAttribute('data-title') || 'Mapa');
        iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
        iframe.setAttribute('allowfullscreen', '');
        iframe.style.width = '100%';
        iframe.style.height = '100%';
        iframe.style.border = '0';
        el.replaceWith(iframe);
      });
    });
  }

  // =====================================================================
  // 19. Wishlist (localStorage)
  // =====================================================================
  var WISHLIST_KEY = 'reblo:wishlist';
  function wishlistRead() {
    try { return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []; }
    catch (e) { return []; }
  }
  function wishlistWrite(arr) {
    try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(arr)); } catch (e) {}
  }
  function syncWishlistButtons() {
    var saved = wishlistRead();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var h = btn.getAttribute('data-wishlist-toggle') || btn.getAttribute('data-product-handle');
      var on = saved.indexOf(h) !== -1;
      btn.classList.toggle('is-saved', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  function initWishlist() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      var handle = btn.getAttribute('data-wishlist-toggle') || btn.getAttribute('data-product-handle');
      if (!handle) return;
      var list = wishlistRead();
      var i = list.indexOf(handle);
      if (i >= 0) list.splice(i, 1); else list.push(handle);
      wishlistWrite(list);
      syncWishlistButtons();
      document.dispatchEvent(new CustomEvent('reblo:wishlist-change', { detail: { wishlist: list, handle: handle } }));
    });
    syncWishlistButtons();
  }
  R.wishlist = { read: wishlistRead, write: wishlistWrite };

  // Wishlist PAGE — renders saved products by AJAX-fetching each product's card
  // section (/products/{handle}?section_id=wishlist-card). localStorage holds
  // handles, so the page is built client-side.
  function initWishlistPage() {
    var page = document.querySelector('[data-wishlist-page]');
    if (!page || page.__rebloWLPage) return;
    page.__rebloWLPage = true;
    var grid = page.querySelector('[data-wishlist-grid]');
    var emptyEl = page.querySelector('[data-wishlist-empty]');
    var loadingEl = page.querySelector('[data-wishlist-loading]');
    var cardSection = page.getAttribute('data-card-section') || 'wishlist-card';
    if (!grid) return;

    function setEmpty(on) {
      if (emptyEl) emptyEl.hidden = !on;
      grid.hidden = on;
    }
    function dropHandle(handle) {
      var list = wishlistRead();
      var i = list.indexOf(handle);
      if (i >= 0) { list.splice(i, 1); wishlistWrite(list); }
    }

    function render() {
      var handles = wishlistRead();
      grid.innerHTML = '';
      if (!handles.length) {
        if (loadingEl) loadingEl.hidden = true;
        setEmpty(true);
        return;
      }
      if (loadingEl) loadingEl.hidden = false;
      setEmpty(false);
      grid.hidden = false;
      var pending = handles.length;
      handles.forEach(function (handle) {
        fetch('/products/' + encodeURIComponent(handle) + '?section_id=' + encodeURIComponent(cardSection), { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : null; })
          .then(function (html) {
            if (!html) { dropHandle(handle); return; } // product gone (404)
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var card = doc.querySelector('[data-product-card]');
            if (card) {
              card.setAttribute('data-wishlist-handle', handle);
              grid.appendChild(card);
            }
          })
          .catch(function () {})
          .finally(function () {
            pending--;
            if (pending === 0) {
              if (loadingEl) loadingEl.hidden = true;
              if (!grid.children.length) setEmpty(true);
              syncWishlistButtons();
              if (R.init) R.init(grid); // re-bind quick-view / lazy etc. on new cards
            }
          });
      });
    }

    // Remove a card when its heart is unticked elsewhere on the page.
    document.addEventListener('reblo:wishlist-change', function (e) {
      var detail = e.detail || {};
      var list = detail.wishlist || wishlistRead();
      var handle = detail.handle;
      if (!handle) return;
      if (list.indexOf(handle) === -1) {
        Array.prototype.slice.call(grid.children).forEach(function (card) {
          if (card.getAttribute('data-wishlist-handle') === handle) card.remove();
        });
        if (!grid.children.length) setEmpty(true);
      } else if (!grid.querySelector('[data-wishlist-handle="' + (window.CSS && CSS.escape ? CSS.escape(handle) : handle) + '"]')) {
        render(); // added while viewing the page
      }
    });

    render();
  }

  // =====================================================================
  // 20. Share button
  // =====================================================================
  function initShare() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-share-button]');
      if (!btn) return;
      e.preventDefault();
      var url = btn.getAttribute('data-url') || window.location.href;
      var title = btn.getAttribute('data-title') || document.title;
      var copiedLabel = btn.getAttribute('data-copied-label') || 'Skopiowano';
      var original = btn.getAttribute('data-original-label') || btn.textContent;

      function flash() {
        var labelEl = btn.querySelector('[data-share-label]') || btn;
        var prev = labelEl.textContent;
        labelEl.textContent = copiedLabel;
        btn.classList.add('is-copied');
        setTimeout(function () { labelEl.textContent = original || prev; btn.classList.remove('is-copied'); }, 1500);
      }

      if (navigator.share) {
        navigator.share({ title: title, url: url }).catch(function () {});
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(flash).catch(function () {});
      } else {
        // Legacy fallback
        var ta = document.createElement('textarea');
        ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); flash(); } catch (e) {}
        ta.remove();
      }
    });
  }

  // =====================================================================
  // 21. Locale form auto-submit
  // =====================================================================
  function initLocaleForms() {
    document.querySelectorAll('[data-locale-form]').forEach(function (form) {
      form.querySelectorAll('select').forEach(function (sel) {
        sel.addEventListener('change', function () {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        });
      });
    });
  }

  // ---------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------
  function initAll(root) {
    initQtySteppers(root);
    initCarousels(root);
    initCenterCarousels(root);
    initAccordions(root);
    initAllTabs(root);
    initVariantPickers(root);
    initCountdowns(root);
    initCountUp(root);
    initVideoFacade(root);
    initMapFacade(root);
  }

  // =====================================================================
  // Header inline search — clicking the magnifier expands a pill input
  // to its left; after 3+ chars we hit /search/suggest.json and show up
  // to 5 matching products. Submitting goes to /search?q=…
  // =====================================================================
  function initHeaderSearch() {
    document.querySelectorAll('[data-header-search]').forEach(function (wrap) {
      if (wrap.dataset.rebloBound === '1') return;
      wrap.dataset.rebloBound = '1';

      var toggle  = wrap.querySelector('[data-header-search-toggle]');
      var input   = wrap.querySelector('[data-header-search-input]');
      var form    = wrap.querySelector('[data-header-search-form]');
      var results = wrap.querySelector('[data-header-search-results]');
      if (!toggle || !input || !form || !results) return;

      var headerInner = wrap.closest('.header-inner');
      var isMobile = function () { return window.matchMedia('(max-width: 749px)').matches; };
      function open() {
        wrap.setAttribute('data-open', '');
        toggle.setAttribute('aria-expanded', 'true');
        input.removeAttribute('tabindex');
        if (headerInner) headerInner.classList.add('header-search-active');
        if (isMobile()) document.documentElement.classList.add('is-locked');
        setTimeout(function () { input.focus(); }, 50);
      }
      function close() {
        wrap.removeAttribute('data-open');
        toggle.setAttribute('aria-expanded', 'false');
        input.setAttribute('tabindex', '-1');
        if (headerInner) headerInner.classList.remove('header-search-active');
        document.documentElement.classList.remove('is-locked');
        hideResults();
      }
      function hideResults() {
        results.setAttribute('hidden', '');
        results.innerHTML = '';
      }

      toggle.addEventListener('click', function (e) {
        // Closed → expand & focus. Open & empty → close. Open & has text → submit (default).
        var isOpen = wrap.hasAttribute('data-open');
        if (!isOpen) {
          e.preventDefault();
          open();
        } else if (input.value.trim() === '') {
          e.preventDefault();
          close();
        }
        // else: let submit go through to /search?q=...
      });

      // Collapse on outside click.
      document.addEventListener('click', function (e) {
        if (!wrap.hasAttribute('data-open')) return;
        // Mobile scrim is a ::before pseudo on wrap itself. A click landing on
        // wrap directly (not on a child element) means the user tapped the
        // scrim area around the floating pill / results — close.
        if (e.target === wrap) { close(); return; }
        // The form + results are moved out of flow visually via position:fixed
        // but remain DOM descendants of wrap, so .contains still includes them.
        if (wrap.contains(e.target)) return;
        close();
      });

      // Collapse on Escape.
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { close(); toggle.focus(); }
      });

      // Predictive search — debounce input.
      var search = debounce(function (q) {
        if (!q || q.length < 3) { hideResults(); return; }
        var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
                  '&resources[type]=product' +
                  '&resources[options][unavailable_products]=last' +
                  '&resources[limit]=5';
        fetch(url, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var products = ((data && data.resources && data.resources.results && data.resources.results.products) || []).slice(0, 5);
            renderResults(products, q);
          })
          .catch(function () { /* swallow */ });
      }, 200);

      function renderResults(products, q) {
        if (!products.length) {
          results.innerHTML = '<p class="hs-empty">Brak wyników dla „' + R.utils.escapeHtml(q) + '"</p>' +
            '<a class="hs-viewall" href="/search?q=' + encodeURIComponent(q) + '">Zobacz wszystkie wyniki →</a>';
          results.removeAttribute('hidden');
          return;
        }
        var items = products.map(function (p) {
          var img = p.image
            ? '<img class="hs-item-img" src="' + R.utils.escapeHtml(p.image) + '" alt="" loading="lazy" width="48" height="48">'
            : '<div class="hs-item-img hs-item-img--ph">—</div>';
          var price = p.price
            ? '<span class="hs-item-price">' + R.utils.formatMoney(Math.round(parseFloat(p.price) * 100)) + '</span>'
            : '';
          var vendor = p.vendor ? '<span class="hs-item-meta">' + R.utils.escapeHtml(p.vendor) + '</span>' : '';
          return '<li><a class="hs-item" href="' + R.utils.escapeHtml(p.url) + '">' +
                 img +
                 '<span class="hs-item-body"><span class="hs-item-title">' + R.utils.escapeHtml(p.title) + '</span>' + vendor + '</span>' +
                 price +
                 '</a></li>';
        }).join('');
        results.innerHTML = '<ul class="hs-list">' + items + '</ul>' +
          '<a class="hs-viewall" href="/search?q=' + encodeURIComponent(q) + '">Zobacz wszystkie wyniki →</a>';
        results.removeAttribute('hidden');
      }

      input.addEventListener('input', function () { search(input.value.trim()); });
      input.addEventListener('focus', function () {
        if (input.value.trim().length >= 3 && !results.firstChild) search(input.value.trim());
      });
    });
  }

  // =====================================================================
  // Collection page — infinite scroll + live title filter.
  // - On scroll, the next /collections/<h>?page=N is fetched and its
  //   product cards are appended to the grid (visible pagination hidden).
  // - The title filter searches across the WHOLE collection: as soon as
  //   the user types, all remaining pages are eagerly fetched in sequence
  //   and the cards are filtered locally by their data-title attribute.
  // - Re-runs on every reblo:collection:updated (facet/sort swap).
  // =====================================================================
  function initCollectionGrid() {
    document.querySelectorAll('[data-collection-page]').forEach(function (sec) {
      var grid = sec.querySelector('[data-collection-grid]');
      if (!grid) return;
      if (grid.dataset.rebloBound === '1') return;
      grid.dataset.rebloBound = '1';

      function findNextUrl(root) {
        var el = (root || sec).querySelector('.pagination a.pagination-next[href]');
        return el ? el.getAttribute('href') : null;
      }
      var nextUrl = findNextUrl();

      // Hide the visible pagination — we replace it with infinite scroll.
      var paginationEl = sec.querySelector('.pagination');
      if (paginationEl) paginationEl.style.display = 'none';

      var loading = false;
      var allLoaded = !nextUrl;

      function fetchPage(url) {
        return fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var newGrid = doc.querySelector('[data-collection-grid]');
            if (newGrid) {
              // Only the outer <li data-product-card> wrappers — NOT the
              // inner <article data-product-card> from the product-card
              // snippet. querySelectorAll('[data-product-card]') matches
              // BOTH per card; appending the article second moves it out
              // of its parent <li> via DOM live-tree semantics, leaving
              // half-empty <li>s + stray <article>s scattered across the
              // grid (the bug from the user screenshot). Scope to li only.
              newGrid.querySelectorAll(':scope > li').forEach(function (li) {
                grid.appendChild(li);
              });
            }
            nextUrl = findNextUrl(doc);
            if (!nextUrl) allLoaded = true;
          });
      }

      function loadNext() {
        if (loading || allLoaded || !nextUrl) return Promise.resolve();
        loading = true;
        sec.classList.add('is-loading-more');
        var url = nextUrl;
        return fetchPage(url)
          .catch(function () { /* swallow — next scroll will retry */ })
          .then(function () {
            loading = false;
            sec.classList.remove('is-loading-more');
            applyTitleFilter();
          });
      }

      function loadAll() {
        function step() {
          if (allLoaded) return Promise.resolve();
          return loadNext().then(step);
        }
        return step();
      }

      // --- Title filter ---
      var input = sec.querySelector('[data-collection-title-filter]');
      var empty = sec.querySelector('[data-collection-title-filter-empty]');
      var clear = sec.querySelector('[data-collection-title-filter-clear]');

      function applyTitleFilter() {
        var q = input ? input.value.trim().toLowerCase() : '';
        var cards = grid.querySelectorAll('[data-product-card]');
        var anyVisible = false;
        cards.forEach(function (card) {
          var title = (card.getAttribute('data-title') || '').toLowerCase();
          var match = !q || title.indexOf(q) !== -1;
          card.hidden = !match;
          if (match) anyVisible = true;
        });
        if (clear) clear.hidden = q === '';
        // Only show the "no matches" message once the whole collection has
        // been loaded — otherwise the user might think it's empty while
        // we're still fetching.
        if (empty) empty.hidden = !(q !== '' && !anyVisible && allLoaded);
      }

      var pendingEagerLoad = null;
      if (input) {
        input.addEventListener('input', function () {
          applyTitleFilter();
          var q = input.value.trim();
          if (q !== '' && !allLoaded && !pendingEagerLoad) {
            // Kick off the full eager-load on first keystroke. While it
            // runs each appended page re-applies the current filter, so
            // matches surface progressively as they come in.
            pendingEagerLoad = loadAll().then(function () {
              pendingEagerLoad = null;
              applyTitleFilter();
            });
          }
        });
        input.addEventListener('search', applyTitleFilter);
        if (clear) {
          clear.addEventListener('click', function () {
            input.value = '';
            applyTitleFilter();
            input.focus();
          });
        }
      }

      // --- Infinite scroll sentinel ---
      var sentinel = sec.querySelector('.coll-scroll-sentinel');
      if (!sentinel) {
        sentinel = document.createElement('div');
        sentinel.className = 'coll-scroll-sentinel';
        sentinel.setAttribute('aria-hidden', 'true');
        grid.insertAdjacentElement('afterend', sentinel);
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries) {
          if (!entries[0].isIntersecting) return;
          // If the user is actively filtering, loadAll is already running.
          var filterActive = input && input.value.trim() !== '';
          if (!filterActive) loadNext();
        }, { rootMargin: '800px 0px' });
        io.observe(sentinel);
      }

      applyTitleFilter();
    });
  }

  // After every AJAX collection update (facet/sort swap), re-init the
  // grid: the inner HTML — including grid, pagination, and filter input
  // — has been replaced and needs fresh bindings.
  document.addEventListener('reblo:collection:updated', function () {
    initCollectionGrid();
  });

  // =====================================================================
  // Blog index — live title filter
  // =====================================================================
  function initBlogFilter() {
    document.querySelectorAll('[data-blog-index]').forEach(function (sec) {
      var input = sec.querySelector('[data-blog-filter]');
      var grid  = sec.querySelector('[data-blog-grid]');
      var empty = sec.querySelector('[data-blog-filter-empty]');
      var clear = sec.querySelector('[data-blog-filter-clear]');
      if (!input || !grid) return;

      var cards = grid.querySelectorAll('[data-article-card]');

      function apply() {
        var q = input.value.trim().toLowerCase();
        var anyVisible = false;
        cards.forEach(function (card) {
          var title = (card.getAttribute('data-title') || '').toLowerCase();
          var match = !q || title.indexOf(q) !== -1;
          card.hidden = !match;
          if (match) anyVisible = true;
        });
        if (clear) clear.hidden = q === '';
        if (empty) empty.hidden = !(q !== '' && !anyVisible);
      }

      input.addEventListener('input', apply);
      input.addEventListener('search', apply);
      if (clear) {
        clear.addEventListener('click', function () {
          input.value = '';
          apply();
          input.focus();
        });
      }
      apply();
    });
  }

  // =====================================================================
  // Sticky product buy bar
  // =====================================================================
  function initStickyProductBar() {
    var bar = document.querySelector('[data-pdp-sticky-buy]');
    if (!bar) return;
    var atcBtn = document.querySelector('[data-add-to-cart]');
    if (!atcBtn) return;

    // The floating CTA adds the selected variant to cart by submitting the real
    // buy-box form (so it goes through cart.js: AJAX add, accessories modal,
    // cart drawer) instead of only scrolling to the form.
    var cta = bar.querySelector('[data-pdp-sticky-cta]');
    if (cta) {
      cta.addEventListener('click', function (e) {
        var scope = bar.closest('.shopify-section') || document;
        var form = scope.querySelector('form.buybox-form');
        if (!form) return; // no form found → fall back to the anchor scroll
        e.preventDefault();
        if (form.requestSubmit) form.requestSubmit();
        else if (atcBtn) atcBtn.click();
      });
    }

    // Show the floating CTA ONLY when the user has scrolled DOWN past the real
    // add-to-cart button (button above the viewport). Never before the user
    // reaches it (button still below the fold).
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var onScreen = entry.isIntersecting && entry.intersectionRatio > 0;
        var scrolledPast = entry.boundingClientRect.top < 0;
        var show = !onScreen && scrolledPast;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      });
    }, { threshold: [0, 0.01], rootMargin: '0px 0px -40px 0px' });
    observer.observe(atcBtn);
  }

  // =====================================================================
  // PDP gallery — thumbnail click swaps the main slide
  // =====================================================================
  function initProductGallery() {
    document.querySelectorAll('[data-pdp-gallery]').forEach(function (gallery) {
      if (gallery.__rebloGallery) return;
      gallery.__rebloGallery = true;
      var main = gallery.querySelector('[data-pdp-gallery-main]');
      var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-slide]'));
      var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-pdp-thumb]'));
      if (!main || !slides.length) return;

      // Initial active slide = the first one.
      slides.forEach(function (s, i) {
        if (i === 0) { s.setAttribute('data-active', ''); s.removeAttribute('aria-hidden'); }
        else { s.removeAttribute('data-active'); s.setAttribute('aria-hidden', 'true'); }
      });

      function activate(mediaId) {
        var idx = -1;
        slides.forEach(function (s, i) {
          var match = String(s.getAttribute('data-media-id')) === String(mediaId);
          if (match) {
            s.setAttribute('data-active', '');
            s.removeAttribute('aria-hidden');
            idx = i;
          } else {
            s.removeAttribute('data-active');
            s.setAttribute('aria-hidden', 'true');
            // Pause any playing media on hidden slides
            var v = s.querySelector('video, iframe');
            if (v && v.tagName === 'VIDEO') { try { v.pause(); } catch (_) {} }
          }
        });
        thumbs.forEach(function (t) {
          t.classList.toggle('is-active', String(t.getAttribute('data-media-id')) === String(mediaId));
        });
        return idx;
      }

      function activateByIndex(i) {
        if (i < 0) i = slides.length - 1;
        if (i >= slides.length) i = 0;
        var s = slides[i];
        if (s) activate(s.getAttribute('data-media-id'));
      }

      // Thumbnail click → swap main image
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          activate(t.getAttribute('data-media-id'));
        });
      });

      // Prev / Next arrows
      var prev = gallery.querySelector('[data-pdp-gallery-prev]');
      var next = gallery.querySelector('[data-pdp-gallery-next]');
      function currentIndex() {
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].hasAttribute('data-active')) return i;
        }
        return 0;
      }
      if (prev) prev.addEventListener('click', function () { activateByIndex(currentIndex() - 1); });
      if (next) next.addEventListener('click', function () { activateByIndex(currentIndex() + 1); });

      // Swipe the main image (mobile / touch + mouse drag) to change slides.
      if (slides.length > 1) {
        main.style.touchAction = 'pan-y';
        var gX = 0, gY = 0, gActive = false;
        main.addEventListener('pointerdown', function (e) {
          gActive = true; gX = e.clientX; gY = e.clientY;
        });
        main.addEventListener('pointerup', function (e) {
          if (!gActive) return;
          gActive = false;
          var dx = e.clientX - gX, dy = e.clientY - gY;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
            activateByIndex(currentIndex() + (dx < 0 ? 1 : -1));
          }
        });
        main.addEventListener('pointercancel', function () { gActive = false; });
      }

      // React to variant changes — swap to the variant's featured media
      document.addEventListener('reblo:variant-media-change', function (e) {
        var m = e && e.detail && e.detail.media;
        if (m && m.id != null) activate(m.id);
      });
    });
  }

  // =====================================================================
  // Tap-to-toggle tooltips on touch devices (hover already works via CSS)
  // =====================================================================
  function initTouchTooltips() {
    var supportsHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    if (supportsHover) return;
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('.has-tooltip');
      // Dismiss any currently-open tooltip first
      document.querySelectorAll('.has-tooltip[data-tooltip-open]').forEach(function (el) {
        if (el !== trigger) el.removeAttribute('data-tooltip-open');
      });
      if (!trigger || !trigger.hasAttribute('data-tooltip')) return;
      // Buttons fire their own action AND show tooltip; non-interactive
      // tooltips (e.g. info pills) toggle on tap.
      var isButton = trigger.tagName === 'BUTTON' || trigger.tagName === 'A';
      if (isButton) return;
      e.preventDefault();
      if (trigger.hasAttribute('data-tooltip-open')) trigger.removeAttribute('data-tooltip-open');
      else trigger.setAttribute('data-tooltip-open', '');
    });
  }

  function boot() {
    initCartDrawer();
    initSearchModal();
    initPredictiveSearch();
    initPriceSliders();
    initCollectionFilters();
    initQuickView();
    initStickyHeader();
    initMobileNav();
    initMegaMenu();
    initBackToTop();
    initLightbox();
    initWishlist();
    initWishlistPage();
    initShare();
    initLocaleForms();
    initHeaderSearch();
    initBlogFilter();
    initCollectionGrid();
    initStickyProductBar();
    initProductGallery();
    initTouchTooltips();
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme Editor: re-init on section load/block select
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    // Some single-instance widgets too
    initStickyHeader();
    initPredictiveSearch();
    initPriceSliders();
  });
  document.addEventListener('shopify:section:unload', function () { /* listeners cleaned via DOM removal */ });
  document.addEventListener('shopify:block:select', function (e) {
    // For carousels: scroll to selected block
    var block = e.target;
    var carousel = block && block.closest && block.closest('[data-carousel-track]');
    if (carousel) {
      carousel.scrollTo({ left: block.offsetLeft, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    }
  });

  // Expose init for ad-hoc rerenders
  R.init = initAll;
})();
